import React, { Dispatch, useState, useMemo, useRef } from 'react';
import { Autocomplete, Stack, TextField, Button, FormControl, FormLabel, Box } from '@mui/material';
import StyledModal from '../../Components/GenericModal/StyledModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Api from '../API';
import {
  ApplicationAction,
  CsvExportWithData,
  CsvExportTemplateItem,
  IApplication
} from '../types';

import { sharedClasses } from '../../Components/CustomUIElements/sharedClasses';
import ModalFooterButtons from '../../Components/GenericModal/ModalFooterButtons';
import { styles } from './styles';
import { classes } from '../../Components/GenericModal/styles';

export default function ExportCsvModal({ dispatch }: { dispatch: Dispatch<ApplicationAction> }) {
  const queryClient = useQueryClient();
  const application = queryClient.getQueryData<IApplication>(['application']);

  const [selectedTemplate, setSelectedTemplate] = useState<{ value: number; label: string } | null>(
    null
  );
  const [candidateData, setCandidateData] = useState<CsvExportTemplateItem[]>([]);
  const [error, setError] = useState<string>('');

  const { data: templates, isLoading: loadingTemplates } = useQuery({
    queryKey: ['csv export templates'],
    queryFn: async () => {
      const { res } = await Api.getCsvExportTemplates();
      const templates = res as { csv_export_templates: Array<{ value: number; label: string }> };
      return templates.csv_export_templates;
    },
    onError: (e: { error: string }) =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error getting export templates: ${e.error}`,
          state: 'error'
        }
      }),
    initialData: [],
    enabled: !!application
  });

  const { isFetching: loadingCandidateData, refetch: fetchCandidateData } = useQuery({
    queryKey: ['csv export candidate data', [application?.id]],
    queryFn: async () => {
      if (!application || !selectedTemplate) return;
      const { res } = await Api.getCandidateDataWithCsvTemplate(
        application.job.id,
        [application.id],
        selectedTemplate.value
      );
      const result = res as CsvExportWithData;

      setCandidateData(
        result.meta.map((row: CsvExportTemplateItem, index: number) => {
          return {
            ...row,
            value: result.csv.rows[0][index]
          };
        })
      );
      return result;
    },
    onError: (e: { error: string }) =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error getting candidate data: ${e.error}`,
          state: 'error'
        }
      }),
    initialData: {
      csv: { rows: [] },
      meta: []
    },
    enabled: false
  });

  const handleDownload = (e: React.FormEvent) => {
    e.preventDefault();
    const headers = candidateData.map((obj) => `"${obj.column}"`).join(',');
    const row1 = candidateData.map((obj) => (obj.value === null ? '' : `"${obj.value}"`)).join(',');
    const csvString = [headers, row1].join('\n');

    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'candidate_export.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    dispatch({
      type: 'SET_SNACKBAR',
      payload: { message: 'CSV file downloaded', state: 'success' }
    });
    handleClose();
  };

  const handleClose = () => dispatch({ type: 'SET_MODALS_OPEN', payload: null });

  const handleInputChange = (index: number, newValue: string) => {
    setCandidateData((prevData) =>
      prevData.map((item, i) => (i === index ? { ...item, value: newValue } : item))
    );
  };

  const formRef = useRef<HTMLFormElement>(null);
  const handleClick = () => {
    if (formRef.current) {
      formRef.current.requestSubmit(); // Triggers native validation
    }
  };

  const CandidateDataDisplay = useMemo(() => {
    if (candidateData.length === 0) return null;
    return candidateData.map((row, index) => {
      if (row.type === 'single_select') {
        return (
          <FormControl sx={{ marginTop: '35px', width: '48%' }} key={index}>
            <Autocomplete
              sx={sharedClasses.formAutocomplete}
              options={row.options ?? []}
              getOptionLabel={(option: { label: string }) => option.label}
              onChange={(_event, value) => handleInputChange(index, value?.value ?? '')}
              renderInput={(params: object) => (
                <TextField
                  {...params}
                  label={row.label}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{
                    ...sharedClasses.autoCompleteTextfield,
                    '& .MuiOutlinedInput-input': { border: 0 }
                  }}
                  required={row.required}
                />
              )}
            />
          </FormControl>
        );
      }
      return (
        <FormControl sx={{ marginTop: '16px', width: '48%' }} key={index}>
          <FormLabel required={row.required} sx={sharedClasses.formLabel}>
            {row.label}
          </FormLabel>
          <TextField
            sx={{
              '& .MuiOutlinedInput-input': {
                padding: '8px',
                border: '1px solid #DDDDDD',
                borderRadius: '6px'
              },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
            }}
            type={row.type === 'date' ? 'date' : 'text'}
            error={false}
            value={row.value}
            required={row.required}
            onChange={(e: { target: { value: string } }) =>
              handleInputChange(index, e.target.value)
            }
          />
        </FormControl>
      );
    });
  }, [candidateData]);

  return (
    <StyledModal
      isOpen={true}
      label="Smart CSV Export"
      handleClose={handleClose}
      styleOverrides={styles.modalStyleOverrides}
    >
      <Stack sx={styles.modalContainer}>
        <Stack sx={styles.modalTitle}>Smart CSV Export</Stack>
        <Stack sx={{ paddingRight: '2px', overflow: 'auto', paddingBottom: 3 }}>
          <Autocomplete
            disablePortal
            options={templates}
            getOptionLabel={(option: { label: string }) => option.label}
            value={selectedTemplate}
            sx={{ ...sharedClasses.formAutocomplete, paddingTop: 4, flexGrow: 1, width: '48%' }}
            loading={loadingTemplates}
            loadingText="Loading templates..."
            renderInput={(params: object) => (
              <TextField
                {...params}
                label="Export to"
                slotProps={{ inputLabel: { shrink: true } }}
                required
                sx={styles.autoCompleteTextfield}
                helperText={error}
              />
            )}
            onChange={(_event, value) => {
              value ? setError('') : setError('Must select a template');
              setSelectedTemplate(value);
            }}
          />

          <Button
            sx={{ ...classes.modalButtonBase, width: 'fit-content', margin: '1rem 0' }}
            disabled={!selectedTemplate || loadingCandidateData}
            onClick={(e) => {
              e.preventDefault();
              fetchCandidateData();
            }}
            loading={loadingCandidateData}
          >
            Gather candidate data
          </Button>
          <form ref={formRef} onSubmit={handleDownload}>
            <Box display="flex" flexWrap="wrap" gap={2}>
              {CandidateDataDisplay}
            </Box>
          </form>
        </Stack>
        <ModalFooterButtons
          primaryButtonText="Export"
          primaryButtonCallback={handleClick}
          secondaryButtonText="Cancel"
          secondaryButtonCallback={handleClose}
          primaryButtonDisabled={candidateData.length === 0}
        />
      </Stack>
    </StyledModal>
  );
}
